import type { LevelPet } from "./levelPet";
import type { LevelSession } from "@highmarch/app";
import type { Command, CommandResult, LevelSim } from "@highmarch/domain";
import type { SceneRig } from "../render/scene";
import type { CardHandView } from "../ui/cardHand";
import type { EnemyCard } from "../ui/enemyCard";
import type { GuideSignal } from "../ui/guide/guide";
import type { HudView } from "../ui/hud";
import type { TowerInfoView } from "../ui/towerInfo";
import type { LevelMoments } from "./announcer";
import type { PendingRewards } from "./impactWaits";
import type { LevelEnd } from "./levelEnd";
import type { LevelGuideHost } from "./levelGuideHost";
import type { LevelLayout } from "./levelLayout";
import type { LevelPause } from "./levelPause";
import type { ScreenPicker } from "./levelPicker";
import type { LevelRally } from "./levelRally";
import type { LevelScene } from "./levelScene";
import type { LevelTaps } from "./levelTaps";
import type { LevelTicks } from "./levelTicks";
import type { PlacementCamera } from "./placementCamera";
import type { RareMoment } from "./rareMoment";
import type { WyrmMoment } from "./wyrmMoment";
import type { SimClock } from "./simClock";
import type { UpgradeAim } from "./upgradeAim";

/**
 * What the level's parts (input, taps, rally, layout, pause, the guide host, the end, the view sync, debug) share, instead of the
 * whole controller: the session, the views and panels, one another, and the three things any part may do — run a
 * command, resync after the sim moved, raise a guide signal. LevelController builds it once; its members are read
 * lazily (getters), since the parts are made one after another in the order their DOM has to be added.
 */
export interface LevelParts {
  readonly session: LevelSession;
  readonly sim: LevelSim;
  readonly rig: SceneRig;
  readonly uiRoot: HTMLElement;
  readonly scene: LevelScene;
  readonly hud: HudView;
  readonly hand: CardHandView;
  readonly towerInfo: TowerInfoView;
  /** The enemy info card (QoL R1): a tap or long-press on an enemy, or a wave chip. */
  readonly enemyCard: EnemyCard;
  /** Eases the view to what is being placed (the road's end and options, the tower cells). */
  readonly camFollow: PlacementCamera;
  readonly picker: ScreenPicker;
  readonly taps: LevelTaps;
  readonly upgrade: UpgradeAim;
  readonly rally: LevelRally;
  readonly layout: LevelLayout;
  readonly pause: LevelPause;
  readonly guide: LevelGuideHost;
  /** Real time → whole sim ticks at the chosen speed; holds while paused. */
  readonly clock: SimClock;
  /** The run's moments: last stand, budgeted callouts, the end verdict (level/announcer.ts, rules in moments.ts). */
  readonly moments: LevelMoments;
  /** Kill rewards whose killing round is still in the air: the points pill counts them when it lands. */
  readonly rewards: PendingRewards;
  /** The level's own readers of each tick's and command's new events. */
  readonly ticks: LevelTicks;
  readonly end: LevelEnd;
  /** M36: the rare spawn's moment (banner, look, music, kill, chest, escape) and the IMMUNE popups. */
  readonly rare: RareMoment;
  /** Pets v2: the pet's HUD chip and the wild pup's moments (level/levelPet.ts). */
  readonly pet: LevelPet;
  /** M40: the Starfall Wyrm's moment (arrival, night, kill and reveal, escape and the choice). */
  readonly wyrm: WyrmMoment;
  /** The game's rebuild for UNDO (LevelController.onUndo); null when it offers none. */
  readonly onUndo: ((session: LevelSession) => void) | null;
  /** The run is over: the end screen is up or on its way. */
  readonly ended: boolean;
  readonly disposed: boolean;
  execute(command: Command): CommandResult;
  afterSimChange(force: boolean, ticked?: boolean): void;
  raise(s: GuideSignal): void;
}

/** How long a placement hint (a missed tap, a card with no room) stays up: long enough to read (DESIGN §4.4). */
export const HINT_MS = 2200;
